import { memo } from "react";
import { FileText, FileSpreadsheet, Image, File } from "lucide-react";
import type { FileAttachment } from "@/types";
import Button from "@mui/material/Button";
import { openS3Url } from "@/api/fileUrl";
import { toast } from "sonner";

const FILE_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  pdf: FileText,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  docx: FileText,
  txt: FileText,
  png: Image,
  jpg: Image,
  jpeg: Image,
  webp: Image,
};

interface FileBubbleProps {
  files: FileAttachment[];
  tone?: "default" | "inverted";
}

export const FileBubble = memo(function FileBubble({
  files,
  tone = "default",
}: FileBubbleProps) {
  if (!files || files.length === 0) return null;

  return (
    <div
      className="flex flex-wrap gap-2 mt-2"
      role="list"
      aria-label="Attached files"
    >
      {files.map((f, i) => {
        const Icon = f.error ? File : (FILE_ICONS[f.fileType] ?? File);
        return (
          <div key={i} role="listitem">
            {f.error ? (
              <span className="inline-flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">
                <File className="h-4 w-4 shrink-0" />
                <span className="max-w-[140px] sm:max-w-[240px] truncate">
                  {f.fileName}
                </span>
                <span className="text-xs opacity-70">({f.error})</span>
              </span>
            ) : (
              <Button
                type="button"
                variant="text"
                className="inline-flex items-center gap-2 cursor-pointer"
                sx={{
                  border: "1px solid",
                  ...(tone === "inverted"
                    ? {
                        backgroundColor: "var(--primary-foreground-15)",
                        borderColor: "var(--primary-foreground-25)",
                        color: "var(--primary-foreground)",
                        "&:hover": {
                          backgroundColor: "var(--primary-foreground-25)",
                        },
                      }
                    : {
                        backgroundColor: "var(--muted)",
                        borderColor: "var(--border)",
                        color: "var(--foreground)",
                        "&:hover": {
                          backgroundColor: "var(--accent)",
                          color: "var(--accent-foreground)",
                        },
                      }),
                }}
                onClick={() => {
                  void openS3Url(f.s3Url).catch(() => {
                    toast.error("Unable to open this file securely");
                  });
                }}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="max-w-[140px] sm:max-w-[240px] truncate">
                  {f.fileName}
                </span>
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
});
